import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useTradingStore } from '../../../../stores/tradingStore';
import { getApiUrl } from '../../../../config/api';
import type { BrokerConnection, Instrument, GridLevels } from '../../../../types/trading';
import './ConfigPanel.css';

// Available strategies
const STRATEGIES = [
  { id: 'grid_bot', name: 'Grid Bot', description: 'Сеточная стратегия' },
  { id: 'dca_bot', name: 'DCA Bot', description: 'Усреднение позиции' },
  { id: 'scalping', name: 'Scalping', description: 'Скальпинг' },
];

interface ConfigPanelProps {
  availableBrokers: BrokerConnection[];
  instruments: Instrument[];
  currentPrice?: number;
  isStarting: boolean;
  error: string;
  onBrokerChange: (brokerKey: string) => void;
  onInstrumentSelect: (figi: string, displayName: string) => void;
  onStartBot: () => void;
  onRunBacktest: () => void;
  onAddBroker: () => void;
  onPreviewGridChange?: (grid: GridLevels | null) => void;
}

export const ConfigPanel: React.FC<ConfigPanelProps> = ({
  availableBrokers,
  instruments,
  currentPrice,
  isStarting,
  error,
  onBrokerChange,
  onInstrumentSelect,
  onStartBot,
  onRunBacktest,
  onAddBroker,
  onPreviewGridChange,
}) => {
  const {
    selectedBroker,
    selectedFuture,
    searchQuery, setSearchQuery,
    strategyId, setStrategyId,
    capital, setCapital,
    gridSize, setGridSize,
    priceRange, setPriceRange,
    minProfit, setMinProfit,
    stopLoss, setStopLoss,
    takeProfit, setTakeProfit,
  } = useTradingStore();

  const [showDropdown, setShowDropdown] = useState(false);
  const [capitalPercent, setCapitalPercent] = useState(60);
  const [lowerPrice, setLowerPrice] = useState('');
  const [upperPrice, setUpperPrice] = useState('');
  const [optionProtection, setOptionProtection] = useState(false);
  const [isFunding, setIsFunding] = useState(false);
  const [fundingMessage, setFundingMessage] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Check if current broker is sandbox
  const currentBrokerIsSandbox = useMemo(() => {
    const broker = availableBrokers.find(b => b.connection_key === selectedBroker);
    return broker?.sandbox ?? false;
  }, [availableBrokers, selectedBroker]);

  // Fund sandbox account
  const handleFundSandbox = async () => {
    if (!selectedBroker || !currentBrokerIsSandbox) return;

    setIsFunding(true);
    setFundingMessage('');

    try {
      const response = await fetch(getApiUrl(`/brokers/${selectedBroker}/sandbox-fund`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ amount: 10000000, currency: 'rub' }),
      });

      if (response.ok) {
        setFundingMessage('Счёт пополнен на 10 млн ₽');
      } else {
        const data = await response.json();
        setFundingMessage(data.error || 'Ошибка пополнения');
      }
    } catch (err) {
      setFundingMessage('Ошибка сети');
    } finally {
      setIsFunding(false);
      setTimeout(() => setFundingMessage(''), 3000);
    }
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDropdown]);

  // Calculate prices based on current price and range
  useEffect(() => {
    if (currentPrice && priceRange) {
      const range = parseFloat(priceRange) / 100;
      setLowerPrice((currentPrice * (1 - range / 2)).toFixed(2));
      setUpperPrice((currentPrice * (1 + range / 2)).toFixed(2));
    }
  }, [currentPrice, priceRange]);

  // Filter and sort instruments (shares/futures first, then by ticker match)
  const filteredInstruments = useMemo(() => {
    // Priority for asset classes
    const assetPriority: Record<string, number> = {
      'share': 1,
      'future': 2,
      'etf': 3,
      'bond': 4,
    };

    let filtered = instruments;

    // If there's a search query, filter by it
    if (searchQuery && searchQuery !== instruments.find(i => i.figi === selectedFuture)?.ticker) {
      const query = searchQuery.toLowerCase();
      filtered = instruments.filter((inst) =>
        inst.ticker.toLowerCase().includes(query) ||
        inst.name.toLowerCase().includes(query)
      );
    }

    return filtered
      .sort((a, b) => {
        if (searchQuery) {
          const query = searchQuery.toLowerCase();
          // Prioritize ticker matches over name matches
          const aTickerMatch = a.ticker.toLowerCase().startsWith(query) ? 0 : 1;
          const bTickerMatch = b.ticker.toLowerCase().startsWith(query) ? 0 : 1;
          if (aTickerMatch !== bTickerMatch) return aTickerMatch - bTickerMatch;
        }

        // Then by asset class priority
        const aPriority = assetPriority[a.asset_class] || 5;
        const bPriority = assetPriority[b.asset_class] || 5;
        return aPriority - bPriority;
      })
      .slice(0, 20);
  }, [instruments, searchQuery, selectedFuture]);

  // Calculate preview grid (only for grid_bot strategy)
  useEffect(() => {
    if (!onPreviewGridChange) return;

    if (strategyId !== 'grid_bot' || !currentPrice) {
      onPreviewGridChange(null);
      return;
    }

    const low = parseFloat(lowerPrice);
    const high = parseFloat(upperPrice);
    const size = parseInt(gridSize);

    if (!isNaN(low) && !isNaN(high) && size >= 2) {
      onPreviewGridChange({ low, high, count: size });
    } else {
      onPreviewGridChange(null);
    }
  }, [lowerPrice, upperPrice, gridSize, currentPrice, onPreviewGridChange, strategyId]);

  const handleInstrumentSelect = (inst: Instrument) => {
    onInstrumentSelect(inst.figi, `${inst.ticker} - ${inst.name}`);
    setSearchQuery(inst.ticker);
    setShowDropdown(false);
  };

  const selectedStrategy = STRATEGIES.find(s => s.id === strategyId) || STRATEGIES[0];

  return (
    <div className="config-panel">
      {/* Header */}
      <div className="config-panel-title">
        <span>Создать бота</span>
        <span className="info-icon">ⓘ</span>
      </div>

      <div className="config-panel-body">
        {/* Trading Account - prices always from production */}
        <div className="config-field">
          <label>Счёт для торговли</label>
          <select
            value={selectedBroker}
            onChange={(e) => onBrokerChange(e.target.value)}
          >
            {availableBrokers.map((broker) => (
              <option key={broker.connection_key} value={broker.connection_key}>
                {broker.broker} {broker.sandbox ? '(Демо)' : '(Боевой)'}
              </option>
            ))}
          </select>
          <small className="config-hint">
            Цены: с боевого счёта • Сделки: с выбранного
          </small>
          {currentBrokerIsSandbox && (
            <div className="sandbox-fund-row">
              <button
                className="sandbox-fund-btn"
                onClick={handleFundSandbox}
                disabled={isFunding}
              >
                {isFunding ? 'Пополнение...' : 'Пополнить демо +10 млн ₽'}
              </button>
              {fundingMessage && (
                <span className="funding-message">{fundingMessage}</span>
              )}
            </div>
          )}
        </div>

        {/* Asset Selector */}
        <div className="config-field">
          <label>Актив</label>
          <div className="asset-selector" ref={dropdownRef}>
            <div
              className={`asset-selector-trigger ${showDropdown ? 'open' : ''}`}
              onClick={() => setShowDropdown(!showDropdown)}
            >
              <div>
                {selectedFuture ? (
                  <div className="asset-selector-value">{searchQuery}</div>
                ) : (
                  <div className="asset-selector-value" style={{ color: 'var(--text-muted)' }}>
                    Выберите актив
                  </div>
                )}
              </div>
              <span className={`asset-selector-arrow ${showDropdown ? 'open' : ''}`}>▼</span>
            </div>

            {showDropdown && (
              <div className="asset-dropdown">
                {/* Search */}
                <div className="asset-search">
                  <span className="asset-search-icon">🔍</span>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Поиск по паре"
                    autoFocus
                  />
                </div>

                {/* Table Header */}
                <div className="asset-table-header">
                  <span>Пара ↕</span>
                  <span>Посл. цена ↕</span>
                  <span>Тип</span>
                </div>

                {/* Asset List */}
                <div className="asset-list">
                  {filteredInstruments.length > 0 ? (
                    filteredInstruments.map((inst) => (
                      <div
                        key={inst.figi}
                        className={`asset-row ${selectedFuture === inst.figi ? 'selected' : ''}`}
                        onClick={() => handleInstrumentSelect(inst)}
                      >
                        <div className="asset-info">
                          <span className="asset-ticker">
                            {inst.ticker}
                            {inst.asset_class && (
                              <span className="asset-type">{inst.asset_class}</span>
                            )}
                          </span>
                          <span className="asset-name">{inst.name}</span>
                        </div>
                        <div className="asset-price">
                          <span className="asset-price-value">—</span>
                        </div>
                        <div className="asset-change">—</div>
                      </div>
                    ))
                  ) : (
                    <div style={{ padding: '16px', textAlign: 'center', color: 'var(--text-muted)' }}>
                      {searchQuery ? 'Ничего не найдено' : 'Загрузка...'}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Strategy */}
        <div className="config-field">
          <label>Стратегия</label>
          <select
            value={strategyId}
            onChange={(e) => setStrategyId(e.target.value)}
          >
            {STRATEGIES.map((strategy) => (
              <option key={strategy.id} value={strategy.id}>
                {strategy.name}
              </option>
            ))}
          </select>
        </div>

        {/* Investment */}
        <div className="config-field">
          <label>Инвестиция, РУБ</label>
          <input
            type="text"
            value={parseFloat(capital).toLocaleString('ru-RU', { minimumFractionDigits: 2 })}
            onChange={(e) => setCapital(e.target.value.replace(/\s/g, '').replace(',', '.'))}
          />
        </div>

        {/* Percent Slider */}
        <div className="config-slider">
          <div className="slider-track">
            <input
              type="range"
              min="0"
              max="100"
              value={capitalPercent}
              onChange={(e) => setCapitalPercent(Number(e.target.value))}
            />
            <div className="slider-fill" style={{ width: `${capitalPercent}%` }} />
          </div>
          <span className="slider-value">{capitalPercent}%</span>
        </div>

        {/* Strategy-specific Settings */}
        <div className="config-section">
          <div className="config-section-title">Настройка {selectedStrategy.name}</div>

          {/* Grid Bot Settings */}
          {strategyId === 'grid_bot' && (
            <>
              <div className="config-row">
                <div className="config-field half">
                  <label>Ниж. цена</label>
                  <input
                    type="text"
                    value={lowerPrice}
                    onChange={(e) => setLowerPrice(e.target.value)}
                  />
                </div>
                <div className="config-field half">
                  <label>Верх. цена</label>
                  <input
                    type="text"
                    value={upperPrice}
                    onChange={(e) => setUpperPrice(e.target.value)}
                  />
                </div>
              </div>

              <div className="config-row">
                <div className="config-field half">
                  <label>Шаг сетки, %</label>
                  <input
                    type="text"
                    value={minProfit}
                    onChange={(e) => setMinProfit(e.target.value)}
                  />
                </div>
                <div className="config-field half">
                  <label>Число уровней</label>
                  <input
                    type="text"
                    value={gridSize}
                    onChange={(e) => setGridSize(e.target.value)}
                  />
                </div>
              </div>
            </>
          )}

          {/* DCA Bot Settings */}
          {strategyId === 'dca_bot' && (
            <>
              <div className="config-row">
                <div className="config-field half">
                  <label>Шаг входа, %</label>
                  <input
                    type="text"
                    value={priceRange}
                    onChange={(e) => setPriceRange(e.target.value)}
                  />
                </div>
                <div className="config-field half">
                  <label>Кол-во ордеров</label>
                  <input
                    type="text"
                    value={gridSize}
                    onChange={(e) => setGridSize(e.target.value)}
                  />
                </div>
              </div>
            </>
          )}

          {/* Scalping Settings */}
          {strategyId === 'scalping' && (
            <>
              <div className="config-row">
                <div className="config-field half">
                  <label>Take Profit, %</label>
                  <input
                    type="text"
                    value={takeProfit}
                    onChange={(e) => setTakeProfit(e.target.value)}
                  />
                </div>
                <div className="config-field half">
                  <label>Stop Loss, %</label>
                  <input
                    type="text"
                    value={stopLoss}
                    onChange={(e) => setStopLoss(e.target.value)}
                  />
                </div>
              </div>
            </>
          )}

          {/* Common: Stop Loss / Take Profit for grid */}
          {strategyId === 'grid_bot' && (
            <div className="config-row">
              <div className="config-field half">
                <label>Stop Loss, %</label>
                <input
                  type="text"
                  value={stopLoss}
                  onChange={(e) => setStopLoss(e.target.value)}
                />
              </div>
              <div className="config-field half">
                <label>Take Profit, %</label>
                <input
                  type="text"
                  value={takeProfit}
                  onChange={(e) => setTakeProfit(e.target.value)}
                />
              </div>
            </div>
          )}
        </div>

        {/* Option Protection */}
        <div className="config-toggle">
          <div className="toggle-info">
            <span className="info-icon small">ⓘ</span>
            <span>Защита опционом</span>
          </div>
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={optionProtection}
              onChange={(e) => setOptionProtection(e.target.checked)}
            />
            <span className="toggle-slider"></span>
          </label>
        </div>

        {error && <div className="config-error">{error}</div>}
      </div>

      {/* Action Button */}
      <div className="config-panel-footer">
        <button
          className="config-action-btn"
          onClick={onStartBot}
          disabled={isStarting || !selectedFuture}
        >
          {isStarting ? 'Запуск...' : 'Запустить бота'}
        </button>
      </div>
    </div>
  );
};

export default ConfigPanel;
